import { iconNavOrdemAzBaixo } from '@bsds-lib-component/icons/icons';
import { BsdsCheckbox, BsdsInput } from '@bsds-lib-component/react';
import React, { useId, useRef } from 'react';

import { Button, ButtonClose, Loading, SystemMessage } from '../../components';
import DrfTermUserModal from '../../components/pagesComponents/companyPageComponents/modal/DrfTermUserModal';
import DrfTermsValidationFailureScreen from '../../components/pagesComponents/companyPageComponents/modal/DrfTermsValidationFailureScreen';
import { useAuth } from '../../hooks/useAuth';
import { useCompany } from '../../hooks/useCompany';
import { formatCnpj } from '../../utils/cnpjUtils';
import { formatCpf } from '../../utils/cpfUtils';
import CompanyApoliceCard, { CompanyApoliceCardGrid } from '../../components/pagesComponents/companyPageComponents/CompanyApoliceCard';

/** Visual-only: CPF (11 digits) or CNPJ (14 alphanumeric — numeric or new alphanumeric). */
const formatDocumentNumber = (value: string | null | undefined): string => {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  const alnum = raw.replace(/[^a-zA-Z0-9]/g, '');
  if (alnum.length === 14) return formatCnpj(alnum);
  if (/^\d{11}$/.test(alnum)) return formatCpf(alnum);
  return raw;
};

/**
 * Business link selection: in reload, `useCompany` hydrates Vida protocol (ramo 4) from `localStorage`
 * (`company.vida.*` via `vidaProtocolStorage`) without new GET until manual confirmation — see `SESSION_CONTROL.md`.
 */
const CompanyPage: React.FC = () => {
  const vinculoSortPanelRef = useRef<HTMLDivElement>(null);
  const {
    vinculosList,
    showCloseToHome,
    pendingItem,
    vinculoConfirmLoading,
    initialVinculosLoading,
    vinculosLoadError,
    autoSelectingPersistedVinculo,
    vinculoConfirmError,
    vinculoSearchTerm,
    setVinculoSearchTerm,
    vinculoSortBy,
    setVinculoSortBy,
    showVinculoSorting,
    setShowVinculoSorting,
    backofficeSearchTerm,
    setBackofficeSearchTerm,
    backofficeSearchProduct,
    backofficeSearchRamo,
    handleBackofficeProductSelect,
    backofficeResult,
    backofficeSelected,
    setBackofficeSelected,
    isBackofficeSession,
    shouldBlockCompanyPageWithWhiteLoading,
    filteredAndSortedVinculos,
    handleVinculoSelect,
    handleVinculoConfirm,
    handleBackofficeSearch,
    handleBackofficeConfirm,
    retryLoadVinculos,
    termsCheckLoading,
    showDrfTermModal,
    showDrfListTermsFailure,
    drfTermsSaveLoading,
    drfTermsSaveError,
    handleDrfTermsSave,
    retryDrfTermsSave,
    retryDrfTermsGate,
    handleDrfTermsExit,
    termContentByTab,
    allDrfTabsViewed,
    drfPortalTermDefinitions,
    selectDrfTermTab,
    userDisplayName,
  } = useCompany(vinculoSortPanelRef);

  const { logout } = useAuth();

  const vinculoSearchInputId = useId();
  const vinculoSortPanelId = useId();
  const vinculoSortSelectId = useId();
  const backofficeSearchInputId = useId();
  const backofficeSearchButtonWrapRef = useRef<HTMLDivElement>(null);

  const canBackofficeSearch =
    !vinculoConfirmLoading &&
    backofficeSearchTerm.trim() !== '' &&
    backofficeSearchProduct != null;

  const triggerBackofficeSearchFromEnter = React.useCallback(() => {
    if (!canBackofficeSearch) return;

    const btn = backofficeSearchButtonWrapRef.current?.querySelector<HTMLElement>(
      'bsds-button, button',
    );
    btn?.focus();
    if (btn && typeof btn.click === 'function') {
      btn.click();
      return;
    }
    void handleBackofficeSearch();
  }, [canBackofficeSearch, handleBackofficeSearch]);

  const handleBackofficeSearchKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      triggerBackofficeSearchFromEnter();
    },
    [triggerBackofficeSearchFromEnter],
  );

  const focusInputById = React.useCallback((id: string) => {
    const el = document.getElementById(id) as
      | (HTMLElement & { setFocus?: () => void | Promise<void>; shadowRoot?: ShadowRoot | null })
      | null;
    if (!el) return;

    if (typeof el.setFocus === 'function') {
      void el.setFocus();
      return;
    }

    const nestedInput =
      (el.shadowRoot?.querySelector('input') as HTMLInputElement | null) ??
      (el.querySelector('input') as HTMLInputElement | null);
    if (nestedInput) {
      nestedInput.focus();
      return;
    }

    el.focus();
  }, []);

  const isDrfTermsBlockingUi = termsCheckLoading || showDrfTermModal || showDrfListTermsFailure;

  if (shouldBlockCompanyPageWithWhiteLoading) {
    return (
      <div
        className="fixed inset-0 z-1000 flex items-center justify-center bg-background-primary"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <Loading />
      </div>
    );
  }

  if (isDrfTermsBlockingUi) {
    return (
      <>
        <div className="fixed inset-0 bg-background-primary" aria-hidden="true" />
        {termsCheckLoading && !showDrfListTermsFailure && (
          <div
            className="fixed inset-0 z-1000 flex items-center justify-center bg-background-primary"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Loading />
          </div>
        )}
        {showDrfListTermsFailure && (
          <DrfTermsValidationFailureScreen
            onRetry={retryDrfTermsGate}
            onExit={handleDrfTermsExit}
            retryLoading={termsCheckLoading}
          />
        )}
        <DrfTermUserModal
          isOpen={showDrfTermModal && !showDrfListTermsFailure}
          userName={userDisplayName}
          portalTermDefinitions={drfPortalTermDefinitions}
          termContentByTab={termContentByTab}
          allTabsViewed={allDrfTabsViewed}
          onTabSelected={selectDrfTermTab}
          onSave={handleDrfTermsSave}
          saveLoading={drfTermsSaveLoading}
          saveError={drfTermsSaveError}
          onRetrySave={retryDrfTermsSave}
        />
      </>
    );
  }

  return (
    <>
      <div className="bg-background-secondary bsds-flex-direction-column" role="main" aria-label="Seleção de vínculo de negócio">
        <div className="bsds-width-100 bg-background-secondary bsds-p-16 bsds-border-bottom">
          {showCloseToHome && (
            <div className="bsds-display-flex bsds-justify-content-end bsds-mb-16">
              <ButtonClose navigatePath="/home" />
            </div>
          )}
           <div className="bsds-display-flex bsds-align-items-center bsds-justify-content-start bsds-mb-8 bsds-mt-8">
              <div className="w-[2.5px] h-8 bg-red-600 bsds-mr-12" aria-hidden="true" />
              <h3 className="bsds-mb-0 bsds-mt-0 leading-none">
                  Vínculos de <span className="font-bold">Negócio</span>
              </h3>
            </div>

          {!isBackofficeSession && vinculosList.length > 0 && (
            <h6 className="bsds-text-sm" style={{ fontSize: '12px', fontWeight: 400 }}>
            Selecione o vínculo com o qual deseja acessar o sistema.
          </h6>
          )}

          {isBackofficeSession && (
            <div className="bsds-mt-16">
              <div
                className="bsds-mb-16"
                role="radiogroup"
                aria-label="Selecione o produto da apólice"
              >
                <span className="text-sm font-medium text-gray-700 bsds-display-block bsds-mb-8">
                  Produto
                </span>
                <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center bsds-gap-24 flex-wrap">
                  <div className="bsds-display-flex bsds-align-items-center bsds-gap-8 bsds-pr-48">
                    <BsdsCheckbox
                      checked={backofficeSearchProduct === 'vida'}
                      onBsdsChange={() => {
                        handleBackofficeProductSelect('vida');
                        window.setTimeout(() => focusInputById(backofficeSearchInputId), 100);
                      }}
                      color="primary"
                      aria-label="Produto Vida"
                    />
                    <span className="bsds-text-regular bsds-pl-12">Vida</span>
                  </div>
                  <div className="bsds-display-flex bsds-align-items-center bsds-gap-8">
                    <BsdsCheckbox
                      checked={backofficeSearchProduct === 'previdencia'}
                      color="primary"
                      disabled
                      aria-label="Produto Previdência"
                    />
                    <span className="bsds-text-regular bsds-pl-12 text-gray-400">Previdência</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <div className="flex-1 relative">
                  <label
                    htmlFor={backofficeSearchInputId}
                    className="text-sm font-medium text-gray-700 bsds-display-block bsds-mb-8"
                  >
                    Pesquise pelo número da apólice
                  </label>
                  <BsdsInput
                    id={backofficeSearchInputId}
                    type="text"
                    placeholder="Pesquise pelo número da apólice"
                    value={backofficeSearchTerm}
                    onInput={(e) => {
                      const target = e.target as HTMLBsdsInputElement;
                      const value =
                        (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value ||
                        target?.value ||
                        '';
                      setBackofficeSearchTerm(String(value));
                    }}
                    onKeyDown={handleBackofficeSearchKeyDown}
                    platform="web"
                    aria-label="Campo de busca por número da apólice"
                  />
                </div>
                <div ref={backofficeSearchButtonWrapRef} className="self-start sm:self-end mb-2">
                  <Button
                    type="button"
                    color="primary"
                    onClick={() => void handleBackofficeSearch()}
                    disabled={!canBackofficeSearch}
                    aria-label="Buscar apólice"
                  >
                    Buscar
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!isBackofficeSession && vinculosList.length > 0 && (
            <div className="bsds-mt-16">
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <div className="flex-1 relative">
                  <label
                    htmlFor={vinculoSearchInputId}
                    className="text-sm font-medium text-gray-700 bsds-display-block bsds-mb-8"
                  >
                    Buscar apólices
                  </label>
                  <BsdsInput
                    id={vinculoSearchInputId}
                    type="text"
                    placeholder="Número da Apólice, Documento, Ramo ou Papel."
                    value={vinculoSearchTerm}
                    onInput={(e) => {
                      const target = e.target as HTMLBsdsInputElement;
                      const value =
                        (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value ||
                        target?.value ||
                        '';
                      setVinculoSearchTerm(String(value));
                    }}
                    platform="web"
                    aria-describedby={`${vinculoSearchInputId}-helper`}
                    aria-label="Campo de busca na lista de apólices"
                  />
                  <span id={`${vinculoSearchInputId}-helper`} className="text-xs text-gray-500 bsds-mt-4 bsds-display-block">
                    Filtra pelos dados do vínculo: Apólice, Documento, Ramo e Papel.
                  </span>
                </div>
                <Button
                  type="button"
                  color="primary"
                  onClick={() => setShowVinculoSorting(!showVinculoSorting)}
                  icon={iconNavOrdemAzBaixo}
                  iconPosition="start"
                  aria-expanded={showVinculoSorting}
                  aria-controls={vinculoSortPanelId}
                  aria-label="Abrir opções de ordenação das apólices"
                  className="self-start sm:self-end mb-6"
                >
                  Ordenação
                </Button>
              </div>
              {showVinculoSorting && (
                <div
                  id={vinculoSortPanelId}
                  ref={vinculoSortPanelRef}
                  className="bsds-mt-16 bsds-width-100 bg-white rounded-lg shadow-md bsds-p-24 border border-gray-100"
                  role="region"
                  aria-label="Ordenação da lista de apólices"
                >
                  <h3 className="text-lg font-semibold text-gray-900 bsds-mb-16">Ordenação</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="bsds-display-block text-sm font-medium text-gray-700 bsds-mb-8" htmlFor={vinculoSortSelectId}>
                        Ordenar por
                      </label>
                      <select
                        id={vinculoSortSelectId}
                        className="bsds-width-100 px-3 py-2 border border-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        value={vinculoSortBy}
                        onChange={(e) => setVinculoSortBy(e.target.value)}
                      >
                        <option value="policy-asc">Número da apólice (A-Z)</option>
                        <option value="policy-desc">Número da apólice (Z-A)</option>
                        <option value="cnpj-asc">Documento (A-Z)</option>
                        <option value="cnpj-desc">Documento (Z-A)</option>
                        <option value="ramo-asc">Ramo (A-Z)</option>
                        <option value="ramo-desc">Ramo (Z-A)</option>
                        <option value="papel-asc">Papel (A-Z)</option>
                        <option value="papel-desc">Papel (Z-A)</option>
                      </select>
                    </div>
                    <div className="bsds-display-flex bsds-align-items-end">
                      <Button
                        color="primary"
                        className="bsds-width-100"
                        type="button"
                        onClick={() => setShowVinculoSorting(false)}
                      >
                        Aplicar ordenação
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="bsds-width-100 bsds-p-16 bsds-position-relative">
          {vinculoConfirmError && (
            <div className="bsds-mb-16" role="alert">
              <SystemMessage message={vinculoConfirmError} type="error" />
            </div>
          )}
          {isBackofficeSession ? (
            !backofficeResult ? (
              <div className="min-h-[50vh] flex items-center justify-center">
                <p className="text-base text-gray-700 font-medium text-center">
                  Busque uma apólice para selecionar e acessar o sistema.
                </p>
              </div>
            ) : (
              <CompanyApoliceCardGrid ariaLabel="Resultado da busca por apólice">
                <CompanyApoliceCard
                  isSelected={backofficeSelected}
                  onSelect={() => setBackofficeSelected(true)}
                  data={{
                    numeroApolice:
                      backofficeResult.numApoliceApol != null ? String(backofficeResult.numApoliceApol) : '—',
                    ramo: backofficeSearchRamo?.ramoNome ?? '—',
                    cnpj: backofficeResult.numDocumentoApol
                      ? formatDocumentNumber(String(backofficeResult.numDocumentoApol)) || '—'
                      : '—',
                  }}
                  onConfirm={handleBackofficeConfirm}
                  confirmDisabled={vinculoConfirmLoading}
                />
              </CompanyApoliceCardGrid>
            )
          ) : initialVinculosLoading || vinculoConfirmLoading ? (
            <div className="min-h-[50vh]" aria-hidden="true" />
          ) : vinculosLoadError ? (
            <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4">
              <p className="text-base text-gray-700 font-medium text-center">
                Erro ao buscar vínculos.
              </p>
              <Button
                type="button"
                color="primary"
                onClick={() => void retryLoadVinculos()}
                disabled={initialVinculosLoading || vinculoConfirmLoading}
              >
                Tente novamente
              </Button>
            </div>
          ) : vinculosList.length === 0 ? (
            <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4">
              <p className="text-base text-gray-700 font-medium text-center">
                Este usuário não contém vínculos de negócio.
              </p>
              <Button type="button" className="bsds-width-25" color="primary" onClick={() => void logout()}>
                Sair
              </Button>
            </div>
          ) : filteredAndSortedVinculos.length === 0 ? (
            <div className="min-h-[40vh] flex items-center justify-center">
              <p className="text-base text-gray-700 font-medium text-center">
                Nenhuma apólice encontrada para a busca.
              </p>
            </div>
          ) : (
            <CompanyApoliceCardGrid ariaLabel="Lista de vínculos">
              {filteredAndSortedVinculos.map((item) => {
                const v = item.vinculo;
                const { parsed } = item;
                const papelDesc = v.representacao?.papel?.descricao ?? '—';
                const isSelected = pendingItem?.vinculo.idVinculoNegocio === v.idVinculoNegocio;
                return (
                  <CompanyApoliceCard
                    key={v.idVinculoNegocio}
                    isSelected={isSelected}
                    onSelect={() => handleVinculoSelect(item)}
                    data={{
                      numeroApolice: parsed.numeroApolice,
                      ramo: parsed.ramoNome,
                      cnpj: formatDocumentNumber(parsed.documento ?? parsed.cnpj) || '—',
                      papel: papelDesc,
                    }}
                    onConfirm={() => handleVinculoConfirm(item)}
                    confirmDisabled={vinculoConfirmLoading}
                  />
                );
              })}
            </CompanyApoliceCardGrid>
          )}
          {vinculoConfirmLoading && (
            <div
              className={`fixed inset-0 z-1000 flex items-center justify-center ${autoSelectingPersistedVinculo ? 'bg-background-primary' : ''
                }`}
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <Loading />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CompanyPage;
